/* eslint-disable @typescript-eslint/no-explicit-any */
import { makeAutoObservable, runInAction } from 'mobx'
import { handleAxiosError } from '~/shared/lib'
import { Loaders } from './types'
import {
  ExcelProduct,
  ProductVariations,
  Orders,
  Cluster,
  ResponceUploadFile,
  InputVariants,
  UniqueCluster,
} from '~/shared/api/services/fbo/types'
import { FBOService } from '~/shared/api/services/fbo'
import { Productavailable } from '~/shared/api/services/orders/types'
import { SelectOption } from '~/shared/ui'
import { GlobalService } from '~/shared/api/services/global'
import { AvailabilitesProduct } from '~/shared/api/services/global/types'

export class FBOStore {
  loaders: Loaders = {
    fbo: false,
    loadMoreFBO: false,
    uploadFile: false,
    createNewOrder: false,
    ordersFBOById: false,
    collectAnOrder: false,
    availabilitesProducts: false,
    addProductForOrder: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  articleFilter = ''

  totalOrdersCount = 0

  manufacturerFilter = ''

  fboOrders: Orders[] = []

  storeId = ''

  responceUploadFile: ExcelProduct[] = []

  clusters: Cluster[] = []

  orderFBOById: ProductVariations[] = []

  selectedCardId: string[] = []

  selectedRows: ExcelProduct[] = []

  availabilitesProducts: AvailabilitesProduct[] = []

  availabilitesProductOptions: Productavailable | null = null

  uniqueClusters: UniqueCluster[] = []

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  getOrdersFBO = async (action?: 'loadMoreFBO') => {
    if (action === 'loadMoreFBO' && this.requestSettings.isAllLoaded) return
    this.activeLoader(action || 'fbo')
    try {
      const params = {
        limit: this.requestSettings.limit,
        offset: action !== 'loadMoreFBO' ? 0 : this.requestSettings.offset,
      }
      const { data, total } = await FBOService.getOrdersFBO({ params })
      if (action !== 'loadMoreFBO') {
        runInAction(() => {
          this.totalOrdersCount = total
        })
      }
      if (data.length !== 0) {
        runInAction(() => {
          this.fboOrders =
            action === 'loadMoreFBO' ? [...this.fboOrders, ...data] : data

          this.requestSettings = {
            ...this.requestSettings,
            isAllLoaded: this.fboOrders.length >= this.totalOrdersCount,
            offset:
              action !== 'loadMoreFBO'
                ? this.requestSettings.limit
                : this.requestSettings.offset + this.requestSettings.limit,
          }
        })
      }
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader(action || 'fbo')
      })
    }
  }

  getOrdersFBOById = async (orderId: string) => {
    this.activeLoader('ordersFBOById')

    try {
      const { data } = await FBOService.getOrdersFBOById(orderId)

      runInAction(() => {
        this.orderFBOById = data.productVariations
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('ordersFBOById')
      })
    }
  }

  uploadFile = async (files: File[]) => {
    this.activeLoader('uploadFile')

    try {
      const formData = new FormData()
      formData.append('storeId', this.storeId)
      formData.append('file', files[0])
      const { data } = await FBOService.uploadFile(formData)
      this.responceUploadFile = data.map((el) => ({
        ...el,
        visibleSubRow: false,
        clusters: el.clusters.map((cl) => ({
          ...cl,
          ownerId: el.productVariationId,
          showInput: '',
          stockQuantity: el.stockQuantity,
          clusterChangeValue: '',
        })),
      }))

      this.setUniqueClusters(data)

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('uploadFile')
      })
    }
  }

  setUniqueClusters = (products: ResponceUploadFile[]) => {
    const clustersMap = new Map<string, { name: string; quantity: string }>()

    products.forEach((product) => {
      product.clusters.forEach((cluster) => {
        if (!clustersMap.has(cluster.name)) {
          clustersMap.set(cluster.name, {
            name: cluster.name,
            quantity: '',
          })
        }
      })
    })

    this.uniqueClusters = Array.from(clustersMap.values())
  }

  getAvailabilitesProducts = async (q?: string) => {
    try {
      this.activeLoader('availabilitesProducts')
      const storeId = localStorage.getItem('storeId')
      if (storeId) {
        const { data } = await GlobalService.getAvailableProducts({
          params: {
            limit: 20,
            offset: 0,
            storeId: JSON.parse(storeId),
            q,
          },
        })
        this.availabilitesProducts = data
      }
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('availabilitesProducts')
      })
    }
  }

  getAvailableProductOptions = async (id: string) => {
    try {
      const { data } = await FBOService.getAvailableProductOptions(id)
      this.availabilitesProductOptions = data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    }
  }

  checkAvailabiliteProductForOrder = async (
    id: string,
    selectedOptions: { [key: string]: SelectOption | null },
  ) => {
    // addProductForOrder
    this.activeLoader('addProductForOrder')
    const storeId = localStorage.getItem('storeId')
    try {
      if (storeId) {
        const { data } = await FBOService.getProductVariation(id, {
          params: {
            optionValues: Object.values(selectedOptions).map((e) =>
              e ? e.value : '',
            ),
          },
        })
        if (
          this.responceUploadFile.some((e) => e.productVariationId === data.id)
        ) {
          return false
        }
        this.responceUploadFile = [
          ...this.responceUploadFile,
          {
            ...data,
            changeValue: 1,
            vendorCode: data.vendor_code,
            visibleSubRow: false,
            clusters: this.uniqueClusters.map((e) => ({
              ...e,
              stockQuantity: data.stockQuantity,
              ownerId: data.id,
            })),
            productVariationId: data.id,
          },
        ]
        return true
      }
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('addProductForOrder')
    }
  }

  setStoreId = (value: string) => {
    this.storeId = value
  }

  setExcelProduct = (
    productVariationId: string,
    name: keyof ExcelProduct,
    newValue: string | boolean | null,
  ) => {
    this.responceUploadFile = this.responceUploadFile.map((el) =>
      el.productVariationId === productVariationId
        ? { ...el, [name]: newValue }
        : el,
    )
  }

  openClusterInput = (
    productVariationId: string,
    clusterName: string,
    inputName: InputVariants,
  ) => {
    this.responceUploadFile = this.responceUploadFile.map((el) =>
      el.productVariationId === productVariationId
        ? {
            ...el,
            clusters: el.clusters.map((cl) =>
              cl.name === clusterName
                ? { ...cl, showInput: inputName }
                : { ...cl, showInput: '' },
            ),
          }
        : el,
    )
  }

  setClusterChangeValue = (
    productVariationId: string,
    clusterName: string,
    value: string,
  ) => {
    this.responceUploadFile = this.responceUploadFile.map((el) =>
      el.productVariationId === productVariationId
        ? {
            ...el,
            clusters: el.clusters.map((cl) =>
              cl.name === clusterName
                ? { ...cl, clusterChangeValue: value }
                : cl,
            ),
          }
        : el,
    )
  }

  setArticleFilter(value: string) {
    this.articleFilter = value
  }

  setManufacturerFilter(value: string) {
    this.manufacturerFilter = value
  }

  resetResponceUploadFile = () => {
    this.responceUploadFile = []
  }

  createNewOrder = async () => {
    try {
      this.activeLoader('createNewOrder')
      await FBOService.createOrder({
        storeId: this.storeId,
        customer: {
          firstname: null,
          lastname: null,
          email: null,
          phone: null,
          fullName: null,
        },
        address: {
          address: null,
          city: null,
          country: 'RU',
          countryZone: null,
          postalCode: null,
        },
        shippingMethod: 'ozon',
        paymentPrice: null,
        comment: null,
        products: this.responceUploadFile.map((el) => ({
          ...el,
          variationId: el.productVariationId,
          quantity: Number(el.changeValue),
          productDetails: {
            productId: null,
            name: el.name,
            options: el.options,
            vendorCode: el.vendorCode,
            brandName: null,
            manufacturerName: el.manufacturer,
            manufacturerId: null,
            available: null,
            optionsText: null,
            optionImage: null,
            length: null,
            height: null,
            width: null,
            stock: null,
            price: null,
            hideInInvoice: null,
            mandatoryMarkField: null,
            count: null,
          },
        })),
        digitalProducts: [],
        label: 'BL',
        parentId: null,
      })
    } catch (errors) {
      const errorMessage = handleAxiosError(errors)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('createNewOrder')
    }
  }

  collectAnOrder = async (id: string) => {
    try {
      this.activeLoader('collectAnOrder')
      await FBOService.collectAnOrder(id, {
        products: this.orderFBOById.map((el) => el.id),
      })
    } catch (error: unknown) {
      const err = error as any
      const message = err?.response?.data?.error
      throw message
    } finally {
      this.closeLoader('collectAnOrder')
    }
  }

  toggleCardSelectionFBO = (id: string) => {
    if (this.selectedCardId.includes(id)) {
      this.selectedCardId = this.selectedCardId.filter((el) => el !== id)
    } else {
      this.selectedCardId = [...this.selectedCardId, id]
    }
  }

  resetSelectedCardId = () => {
    this.selectedCardId = []
  }

  setSelectedRows = (arr: ExcelProduct[]) => {
    this.selectedRows = arr
  }

  setProductsList = (arr: ExcelProduct[]) => {
    this.responceUploadFile = arr
  }

  deleteCluster = (productId: string, name: string) => {
    this.responceUploadFile = this.responceUploadFile.map((product) => {
      if (product.productVariationId || product.product_id === productId) {
        const newClusters = product.clusters.filter((e) => e.name !== name)
        return {
          ...product,
          clusters: newClusters,
          stockQuantity: newClusters.reduce(
            (sum, c) => sum + Number(c.quantity),
            0,
          ),
        }
      }
      return product
    })
  }

  updatingBalances = async () => {
    try {
      const { data } = await FBOService.updatingBalances(
        this.responceUploadFile.map((e) => e.productVariationId),
        this.storeId,
      )
      this.responceUploadFile = this.responceUploadFile.map((el) => {
        const findNewData = data.find(
          (dataEl) => el.productVariationId === dataEl.productVariationId,
        )
        if (findNewData) {
          return {
            ...el,
            stockQuantity: findNewData.stockQuantity,
            clusters: el.clusters.map((cl) => ({
              ...cl,
              stockQuantity: findNewData.stockQuantity,
            })),
          }
        }
        return el
      })
    } catch (error: unknown) {
      const err = error as any
      const message = err?.response?.data?.error
      throw message
    }
  }
}

export default FBOStore
